import { readAdminRegionCookie } from "@/lib/admin-region";
import { refreshAccessToken } from "@/lib/auth-session";
import { getApiBase } from "@/lib/env";
import { useAuthStore } from "@/stores/auth-store";

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const doFetch = (accessToken: string | null) => {
    const headers = new Headers(init?.headers);
    const body = init?.body;
    if (body && typeof body === "string" && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    if (!headers.has("X-Admin-Region")) {
      headers.set("X-Admin-Region", readAdminRegionCookie());
    }
    return fetch(`${getApiBase()}${path}`, {
      ...init,
      headers,
    });
  };

  const token = useAuthStore.getState().token;
  let res = await doFetch(token);

  if (
    res.status === 401 &&
    path !== "/admin/auth/refresh" &&
    path !== "/admin/auth/login"
  ) {
    const ok = await refreshAccessToken();
    if (ok) {
      res = await doFetch(useAuthStore.getState().token);
    }
  }

  if (
    res.status === 401 &&
    path !== "/admin/auth/login" &&
    path !== "/admin/auth/refresh"
  ) {
    useAuthStore.getState().logout();
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login")
    ) {
      window.location.replace("/login");
    }
  }

  return res;
}

export function summarizeHttpError(status: number, body: string): string {
  const t = body.trim();
  if (!t) return `Request failed (HTTP ${status}).`;
  if (t.startsWith("{")) {
    try {
      const j = JSON.parse(t) as {
        title?: string;
        detail?: string;
        message?: string;
        errors?: Record<string, string[]>;
      };
      if (j.errors && typeof j.errors === "object") {
        const lines = Object.entries(j.errors).flatMap(([k, v]) =>
          (Array.isArray(v) ? v : [String(v)]).map((m) => `${k}: ${m}`)
        );
        if (lines.length > 0) return lines.join("\n");
      }
      const msg = j.detail ?? j.message ?? j.title;
      if (msg) return msg;
    } catch {
      /* fall through */
    }
  }
  if (t.includes("Internal Server Error") || t.length > 200) {
    return `Server error (HTTP ${status}). Check the API is running at ${getApiBase()} and logs for details.`;
  }
  return t.length > 400 ? `${t.slice(0, 400)}…` : t;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    const res = await apiFetch(path, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(summarizeHttpError(res.status, text));
    }
    const ct = res.headers.get("content-type");
    if (!ct?.includes("application/json")) {
      return undefined as T;
    }
    return res.json() as Promise<T>;
  } catch (e) {
    if (e instanceof TypeError) {
      throw new Error(
        `Network error (failed to reach API at ${getApiBase()}). Check the API is up, CORS, and proxy timeouts — this is not an ERP vendor field error.`
      );
    }
    throw e;
  }
}

/** True when ERP status is a clear success (legacy/vendor often return "success"). */
export function isErpPushSuccess(status: string | null | undefined): boolean {
  const s = (status ?? "").trim().toLowerCase();
  return s === "success" || s === "ok" || s === "true";
}
